<template>
  <v-dialog
    v-model="dialog"
    max-width="700px"
    scrollable
    persistent
  >
    <v-card>
      <v-card-title>
        <span class="headline">{{ $t('manage_companies_for_customer', { name: customerName }) }}</span>
        <v-spacer></v-spacer>
        <v-btn icon @click="closeModal">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="pa-4">

        <!-- Add new relation -->
        <v-row align="center" class="mb-4">
          <v-col cols="9">
            <v-autocomplete
              v-model="selectedCompanyToAdd"
              :items="availableCompanies"
              :item-text="companyLabel"
              item-value="company_id"
              :label="$t('select_company_to_add')"
              :loading="loadingAvailable"
              outlined
              dense
              clearable
              hide-details
              return-object
            ></v-autocomplete>
          </v-col>
          <v-col cols="2">
            <v-btn
              color="primary"
              :disabled="!selectedCompanyToAdd"
              :loading="adding"
              @click="addRelation"
              block
            >
              <v-icon left>mdi-link-plus</v-icon>
              {{ $t('add') }}
            </v-btn>
          </v-col>
        </v-row>

        <v-divider class="mb-4"></v-divider>

        <!-- Current relations table -->
        <v-data-table
          :headers="tableHeaders"
          :items="linkedCompanies"
          class="elevation-1 compact-table"
          :loading="loadingLinked"
          hide-default-footer
          disable-pagination
        >
          <template v-slot:[`item.name`]="{ item }">
            {{ companyLabel(item) }}
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  :loading="removingId === item.company_id"
                  @click="removeRelation(item)"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('remove_company_link') }}</span>
            </v-tooltip>
          </template>

          <template v-slot:no-data>
            <span class="grey--text">{{ $t('no_items_found') }}</span>
          </template>
        </v-data-table>

      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="grey" text @click="closeModal">
          {{ $t('close') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
  ::v-deep .compact-table .v-data-table__wrapper td,
  ::v-deep .compact-table .v-data-table__wrapper th {
    padding: 6px 8px !important;
  }
</style>

<script>
import { mapActions } from 'vuex'
import AppActions from '@/store/app/actions-types'
import CustomerAPI from '@/services/api/business/customer'
import CompanyCustomerAPI from '@/services/api/business/company-customer'
import CompanyCoreAPI from '@/services/api/core/company'
import TenantAPI from '@/services/api/core/tenant'

export default {
  name: 'CompanyCustomerModal',

  data() {
    return {
      dialog: false,
      customerId: null,
      customerName: '',
      linkedIds: [],
      linkedCompanies: [],
      allCompanies: [],
      availableCompanies: [],
      tenantCompanyIds: [],
      selectedCompanyToAdd: null,
      loadingLinked: false,
      loadingAvailable: false,
      adding: false,
      removingId: null,
    }
  },

  computed: {
    tableHeaders() {
      return [
        { text: this.$t('company_name'), value: 'name', sortable: true },
        { text: this.$t('actions'), value: 'actions', sortable: false },
      ]
    },
  },

  methods: {
    ...mapActions('app', [
      AppActions.OPEN_APP_SUCCESS_MESSAGE,
    ]),

    openModal(customer) {
      this.customerId = customer.customer_id
      this.customerName = customer.customer_name
      this.linkedIds = []
      this.linkedCompanies = []
      this.allCompanies = []
      this.availableCompanies = []
      this.selectedCompanyToAdd = null
      this.dialog = true
      this.loadData()
    },

    closeModal() {
      this.dialog = false
    },

    loadData() {
      this.loadLinked()
      this.loadAllCompanies()
      this.loadTenants()
    },

    loadLinked() {
      this.loadingLinked = true
      CustomerAPI.getAllCompaniesByCustomer(this.customerId)
        .then((result) => {
          // Endpoint returns a plain array of company IDs: [1, 2, 3, ...]
          const raw = result.data.items || result.data || []
          this.linkedIds = raw.map(id => Number(id))
          this.loadingLinked = false
          this.refreshAvailableFilter()
        })
        .catch(() => {
          this.loadingLinked = false
        })
    },

    loadTenants() {
      TenantAPI.getAllTenants({ limit: 1000, offset: 0 })
        .then((result) => {
          const tenants = result.data.items || result.data || []
          this.tenantCompanyIds = tenants.map(t => Number(t.company_id))
        })
        .catch(() => {})
    },

    loadAllCompanies() {
      this.loadingAvailable = true
      CompanyCoreAPI.getAllCompanies({ limit: 1000, offset: 0 })
        .then((result) => {
          this.allCompanies = result.data.items || result.data || []
          this.loadingAvailable = false
          this.refreshAvailableFilter()
        })
        .catch(() => {
          this.loadingAvailable = false
        })
    },

    refreshAvailableFilter() {
      // Build full company objects for linked companies using the ID list
      this.linkedCompanies = this.allCompanies.filter(c => this.linkedIds.includes(Number(c.company_id)))
      this.availableCompanies = this.allCompanies.filter(c => !this.linkedIds.includes(Number(c.company_id)))
    },

    companyLabel(company) {
      if (!company) return ''
      const isTenant = this.tenantCompanyIds.includes(Number(company.company_id))
      return `${company.name} (${isTenant ? 'tenant' : 'company'})`
    },

    addRelation() {
      if (!this.selectedCompanyToAdd) return

      this.adding = true
      const companyId = this.selectedCompanyToAdd.company_id

      CompanyCustomerAPI.createCompanyCustomer(companyId, this.customerId)
        .then(() => {
          this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t('company_customer_added_successfully'))
          this.selectedCompanyToAdd = null
          this.adding = false
          this.loadLinked()
        })
        .catch((error) => {
          this.adding = false
          console.error('Error adding company-customer relation:', error)
        })
    },

    removeRelation(company) {
      this.removingId = company.company_id

      CompanyCustomerAPI.deleteCompanyCustomer(company.company_id, this.customerId)
        .then(() => {
          this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t('company_customer_removed_successfully'))
          this.removingId = null
          this.loadLinked()
        })
        .catch((error) => {
          this.removingId = null
          console.error('Error removing company-customer relation:', error)
        })
    },
  },
}
</script>
